import { IParsedDictionary } from 'helpers/dictionary';
import {
  ISearchFilterAccessory,
  ISearchFilterAmmunition,
  ISearchFilterAncillaries,
  ISearchFilterComponent,
} from 'models/searchFilter';
import { clearObject } from 'helpers/objects';
import { EArtAncillaryType, EArtAncillaryTypeNameLowerCase } from 'constants/ArtAccessoryType';
import { ESearchFilterKeys } from 'constants/SearchFilterTypes';
import { formatOnlyDateToRequest, simpleFormatDate } from 'utils/date';
import { parse } from 'date-fns';

const mapValuesToAccessoryPresetRequest = (
  data: ISearchFilterAccessory,
): ISearchFilterAccessory => {
  const values: ISearchFilterAccessory = {
    ancillaryDictionaryUniqueId: EArtAncillaryType.ACCESSORIES,
    hostTypesUniqueIds: data.hostTypesUniqueIds,
    magnificationsUniqueIds: data.magnificationsUniqueIds,
    utilitiesUniqueIds: data.utilitiesUniqueIds,
    reticlesUniqueIds: data.reticlesUniqueIds,
    lightColoursUniqueIds: data.lightColoursUniqueIds,
    accessoryCapacities: data.accessoryCapacities,
    ancillaryTypeUniqueId: data.ancillaryTypeUniqueId,
    manufacturersUniqueIds: data.manufacturersUniqueIds,
    calibresUniqueIds: data.calibresUniqueIds,
    serialNumber: data.serialNumber,
    marking: data.marking,
    compositionsUniqueIds: data.compositionsUniqueIds,
    proofHousesUniqueIds: data.proofHousesUniqueIds,
    artId: data.artId,
    exhibitNumber: data.exhibitNumber,
    statesUniqueIds: data.statesUniqueIds,
    legalityUniqueId: data.legalityUniqueId,
    ownerUniqueId: data.ownerUniqueId,
    keeperUniqueId: data.keeperUniqueId,
    fireCycleId: data.fireCycleId,
    productName: data.productName,
  };

  return clearObject(values);
};

export const permittedAccessoryFilters = [
  'compositionsUniqueIds',
  'hostTypesUniqueIds',
  'magnificationsUniqueIds',
  'utilitiesUniqueIds',
  'reticlesUniqueIds',
  'lightColoursUniqueIds',
  'accessoryCapacities',
  'accessoryCapacity',
  'ancillaryTypeUniqueId',
  'manufacturersUniqueIds',
  'calibresUniqueIds',
  'serialNumber',
  'calibresUniqueIds',
  'proofHousesUniqueIds',
  'exhibitNumber',
  'statesUniqueIds',
  'legalityUniqueId',
  'ownerName',
  'ownerUniqueId',
  'keeperName',
  'keeperUniqueId',
  'fireCycleId',
  'productName',
  'marking',
];

const mapValuesToAmmunitionPresetRequest = (
  data: ISearchFilterAmmunition,
): ISearchFilterAmmunition => {
  const values: ISearchFilterAmmunition = {
    ancillaryDictionaryUniqueId: EArtAncillaryType.AMMUNITION,
    fireCycleId: data.fireCycleId,
    lotNumber: data.lotNumber,
    batchNumber: data.batchNumber,
    factoriesUniqueIds: data.factoriesUniqueIds,
    manufactureDateFrom: data.manufactureDateFrom,
    manufactureDateTo: data.manufactureDateTo,
    functionalTypesUniqueIds: data.functionalTypesUniqueIds,
    cartridgeCompositionsUniqueIds: data.cartridgeCompositionsUniqueIds,
    coloursOfPrimerSealantUniqueIds: data.coloursOfPrimerSealantUniqueIds,
    projectileShapesUniqueIds: data.projectileShapesUniqueIds,
    shotSizesUniqueIds: data.shotSizesUniqueIds,
    munitionTypesUniqueIds: data.munitionTypeUniqueId
      ? [data.munitionTypeUniqueId]
      : data.munitionTypesUniqueIds,
    munitionVelocitiesUniqueIds: data.munitionVelocitiesUniqueIds,
    manufacturersUniqueIds: data.manufacturersUniqueIds,
    headstampYear: data.headstampYear,
    alphanumericalMarkins: data.alphanumericalMarkins,
    ancillaryTypeUniqueId: data.ancillaryTypeUniqueId,
    calibresUniqueIds: data.calibresUniqueIds,
    serialNumber: data.serialNumber,
    marking: data.marking,
    compositionsUniqueIds: data.compositionsUniqueIds,
    proofHousesUniqueIds: data.proofHousesUniqueIds,
    expiryDateFrom: data.expiryDateFrom,
    expiryDateTo: data.expiryDateTo,
    artId: data.artId,
    exhibitNumber: data.exhibitNumber,
    statesUniqueIds: data.statesUniqueIds,
    legalityUniqueId: data.legalityUniqueId,
    ownerUniqueId: data.ownerUniqueId,
    keeperUniqueId: data.keeperUniqueId,
    productName: data.productName,
  };
  return clearObject(values);
};

export const permittedAmmunitionFilters = [
  'fireCycleId',
  'lotNumber',
  'batchNumber',
  'factoriesUniqueIds',
  'manufactureDateFrom',
  'manufactureDateTo',
  'functionalTypesUniqueIds',
  'cartridgeCompositionsUniqueIds',
  'coloursOfPrimerSealantUniqueIds',
  'projectileShapesUniqueIds',
  'shotSizesUniqueIds',
  'munitionTypesUniqueIds',
  'munitionTypeUniqueId',
  'munitionVelocitiesUniqueIds',
  'headstampYear',
  'alphanumericalMarkins',
  'ancillaryTypeUniqueId',
  'manufacturersUniqueIds',
  'calibresUniqueIds',
  'serialNumber',
  'compositionsUniqueIds',
  'proofHousesUniqueIds',
  'expiryDateFrom',
  'expiryDateTo',
  'exhibitNumber',
  'statesUniqueIds',
  'legalityUniqueId',
  'ownerName',
  'ownerUniqueId',
  'keeperName',
  'keeperUniqueId',
  'productName',
  'marking',
];

const mapValuesToComponentPresetRequest = (
  data: ISearchFilterComponent,
): ISearchFilterComponent => {
  const values: ISearchFilterComponent = {
    ancillaryDictionaryUniqueId: EArtAncillaryType.COMPONENTS,
    frtTypesUniqueIds: data.frtTypesUniqueIds,
    frtMakesUniqueIds: data.frtMakesUniqueIds,
    frtModelsUniqueIds: data.frtModelsUniqueIds,
    frtManufacturersUniqueIds: data.frtManufacturersUniqueIds,
    componentCapacitiesUniqueIds: data.componentCapacitiesUniqueIds,
    length: data.length,
    manufacturersUniqueIds: data.manufacturersUniqueIds,
    ancillaryTypeUniqueId: data.ancillaryTypeUniqueId,
    calibresUniqueIds: data.calibresUniqueIds,
    serialNumber: data.serialNumber,
    marking: data.marking,
    compositionsUniqueIds: data.compositionsUniqueIds,
    proofHousesUniqueIds: data.proofHousesUniqueIds,
    artId: data.artId,
    exhibitNumber: data.exhibitNumber,
    fireCycleId: data.fireCycleId,
    statesUniqueIds: data.statesUniqueIds,
    legalityUniqueId: data.legalityUniqueId,
    ownerUniqueId: data.ownerUniqueId,
    keeperUniqueId: data.keeperUniqueId,
    productName: data.productName,
  };

  return clearObject(values);
};

export const permittedComponentFilters = [
  'frtTypesUniqueIds',
  'frtMakesUniqueIds',
  'frtModelsUniqueIds',
  'frtManufacturersUniqueIds',
  'componentCapacitiesUniqueIds',
  'manufacturersUniqueIds',
  'length',
  'ancillaryTypeUniqueId',
  'manufacturerUniqueId',
  'frtTypesUniqueIds',
  'calibresUniqueIds',
  'serialNumber',
  'compositionsUniqueIds',
  'proofHousesUniqueIds',
  'exhibitNumber',
  'fireCycleId',
  'statesUniqueIds',
  'legalityUniqueId',
  'ownerName',
  'ownerUniqueId',
  'keeperName',
  'keeperUniqueId',
  'productName',
  'marking',
];

export const mapValuesToAncillariesPresetRequest = (
  data: ISearchFilterAncillaries,
  type: ESearchFilterKeys | EArtAncillaryTypeNameLowerCase,
  dateFormat: string,
): ISearchFilterAccessory | ISearchFilterAmmunition | ISearchFilterComponent | null => {
  if (!type) {
    return null;
  }
  const newData = { ...data };

  newData.manufactureDateFrom = data.manufactureDateFrom
    ? formatOnlyDateToRequest(data.manufactureDateFrom)
    : '';
  newData.manufactureDateTo = data.manufactureDateTo
    ? formatOnlyDateToRequest(data.manufactureDateTo)
    : '';
  newData.expiryDateFrom = data.expiryDateFrom
    ? formatOnlyDateToRequest(data.expiryDateFrom)
    : '';
  newData.expiryDateTo = data.expiryDateTo ? formatOnlyDateToRequest(data.expiryDateTo) : '';
  switch (type) {
    case ESearchFilterKeys.accessory:
    case EArtAncillaryTypeNameLowerCase.ACCESSORY:
      return mapValuesToAccessoryPresetRequest(data);
    case ESearchFilterKeys.ammunition:
    case EArtAncillaryTypeNameLowerCase.AMMUNITION:
      return mapValuesToAmmunitionPresetRequest(newData);
    case ESearchFilterKeys.component:
    case EArtAncillaryTypeNameLowerCase.COMPONENTS:
      return mapValuesToComponentPresetRequest(data);
    default:
      return null;
  }
};

export const mapValuesToAncillaryPresetForView = (
  values: { key: string; value: string }[],
  shortDateFormat: string,
) =>
  values.map((item) => {
    switch (item.key) {
      case 'manufactureDateFrom':
      case 'manufactureDateTo':
      case 'expiryDateFrom':
      case 'expiryDateTo':
        return {
          key: item.key,
          value: simpleFormatDate(item.value, shortDateFormat),
        };
      default:
        return item;
    }
  });
